import React, { useState } from "react";
import { Box, Typography, useTheme, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { routes } from "../routes/routes";
import useDeleteQuestionnaire from "../hooks/useDeleteQuestionnaire";
import useUnlinkQuestionnaire from "../hooks/useUnlinkQuestionnaire";
import ConfirmationModal from "./Modal/ConfirmationModal";

const QuestionnaireSummaryCard = ({ questionnaireItem, onDelete, onError, parent }) => {
  const ORIGIN_TEXT = {
    instrument: "del instrumento",
    application: "de la aplicación",
  };

  const theme = useTheme();
  const navigate = useNavigate();
  const { deleteQuestionnaire, loading: deleteLoading } = useDeleteQuestionnaire();
  const { unlinkQuestionnaire, loading: unlinkLoading } = useUnlinkQuestionnaire();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showUnlinkConfirmModal, setShowUnlinkConfirmModal] = useState(false);

  const handleViewClick = () => {
    navigate(routes.cuestionarioPreview.replace(":id", questionnaireItem.id), {
      state: { isEditing: false },
    });
  };

  const handleEditClick = () => {
    navigate(routes.cuestionarioPreview.replace(":id", questionnaireItem.id), {
      state: { isEditing: true },
    });
  };

  const handleUnlinkClick = () => {
    setShowUnlinkConfirmModal(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmUnlink = async () => {
    setIsUnlinking(true);
    try {
      await unlinkQuestionnaire({ questionnaireId: questionnaireItem.id, parent });
      if (onDelete) {
        onDelete(questionnaireItem.id, "unlink");
      }
      setShowUnlinkConfirmModal(false);
    } catch (error) {
      const errorMsg = typeof error === "string" ? error : error?.message || "Error desconocido al desvincular el cuestionario";
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteQuestionnaire(questionnaireItem.id);
      setShowDeleteConfirmModal(false);
      if (onDelete) {
        onDelete(questionnaireItem.id);
      }
    } catch (error) {
      console.log(error);
      const errorMsg = typeof error === "string" ? error : error?.message || "Error desconocido al eliminar el cuestionario";
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        bgcolor: "background.paper",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: theme.shadows[2] },
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, rowGap: 1, flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          ID:
        </Typography>
        <Typography variant="body2">{questionnaireItem.id}</Typography>

        <Typography variant="body2" color="text.secondary">
          ID Externo:
        </Typography>
        <Typography variant="body2">{questionnaireItem.external_id || ""}</Typography>

        <Typography variant="body2" color="text.secondary">
          Título:
        </Typography>
        <Typography variant="body2">{questionnaireItem.title}</Typography>

        <Typography variant="body2" color="text.secondary">
          Tipo:
        </Typography>
        <Typography variant="body2">{questionnaireItem.type}</Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 2 }}>
        <Button variant="contained" size="small" onClick={handleViewClick} sx={{ textTransform: "none", flex: 1 }}>
          Ver
        </Button>
        <Button variant="contained" size="small" onClick={handleEditClick} sx={{ textTransform: "none", flex: 1 }}>
          Editar
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleUnlinkClick}
          disabled={isUnlinking || unlinkLoading}
          sx={{ textTransform: "none", flex: 1 }}
        >
          {isUnlinking ? "Desvinculando..." : "Desvincular"}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleDeleteClick}
          disabled={isDeleting || deleteLoading}
          sx={{ textTransform: "none", flex: 1 }}
        >
          {isDeleting ? "Eliminando..." : "Eliminar"}
        </Button>
      </Box>

      <ConfirmationModal
        open={showDeleteConfirmModal}
        handleClose={() => setShowDeleteConfirmModal(false)}
        handleConfirm={handleConfirmDelete}
        title={`Eliminar cuestionario ${questionnaireItem.external_id} ${ORIGIN_TEXT[parent.type] || ""}`}
        description="¿Está seguro? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
      <ConfirmationModal
        open={showUnlinkConfirmModal}
        handleClose={() => setShowUnlinkConfirmModal(false)}
        handleConfirm={handleConfirmUnlink}
        title={`Desvincular cuestionario ${questionnaireItem.external_id} ${ORIGIN_TEXT[parent.type] || ""}`}
        description="¿Está seguro? Esta acción no se puede deshacer."
        confirmText="Desvincular"
        cancelText="Cancelar"
      />
    </Box>
  );
};

export default QuestionnaireSummaryCard;
